<template>
  <div>
    <LoadingIndicator v-if="loading" />
    <div
      v-else-if="!!content.length"
      id="news-container"
    >
      <news
        :theme="themeSettings"
        :content="content"
        :news-config="config"
        :component-id="componentId"
        :path-prefix="pathPrefix"
        :fallback-image="fallbackImage"
        :is-preview="isThemePreview"
      />
      <DotsRectangle
        v-if="dotsRectangle.show"
        :fill-color="dotsRectangle.color"
        :flip-horizontal="dotsRectangle.flip"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, ref } from 'vue';
import { useMainStore } from '../../../stores/mainStore.js';
import { useTranslationStore } from '../../../stores/translationStore.js';
import LoadingIndicator from '../../global/LoadingIndicator.vue';
import DotsRectangle from '../theme_accents/svg/DotsRectangle.vue';
import HomepageFetchHelper from '../../../helpers/homepage-fetcher.js';

defineOptions({
  name: 'ViewNews',
});

const props = defineProps({
  containerIdx: {
    type: Number,
    default: null,
  },
  componentIdx: {
    type: Number,
    default: null,
  },
  containerId: {
    type: String,
    default: null,
  },
  dotsRectangle: {
    type: Object,
    default: () => ({}),
  },
});

const mainStore = useMainStore();
const translationStore = useTranslationStore();

/** Captured in onMounted so async fetch paths keep the same $axios / $staticFetch after await (getCurrentInstance() is null after await). */
const nuxtGlobs = { axios: null, staticFetch: null };

const loading = ref(true);
const content = ref([]);
const config = ref({});

const homepage = computed(() => mainStore.homepage);
const theme = computed(() => mainStore.theme);
const pathPrefix = computed(() => mainStore.pathPrefix);
const isThemePreview = computed(() => mainStore.isThemePreview);
const featureFlags = computed(() => mainStore.featureFlags);
const settings = computed(() => mainStore.settings);
const staticFilesConfig = computed(() => mainStore.staticFilesConfig);
const mediaPreviewContent = computed(() => mainStore.mediaPreviewContent);
const globals = computed(() => mainStore.globals);

const defaultData = computed(() => mediaPreviewContent.value?.newsPreviewContent || []);

const themeSettings = computed(() => {
  if (!theme.value?.containers) return {};
  return {
    ...theme.value.containers[props.containerIdx].components[props.componentIdx].options,
    translations: translationStore.getComponentTranslation('news'),
    globalThemeSettings: mainStore.getGlobalThemeSettings,
  };
});

const componentId = computed(() => {
  if (isThemePreview.value) {
    return 'preview_id';
  }

  return `${props.containerId}_${props.componentIdx}`;
});

const useS3ForMedia = computed(() => featureFlags.value.cms_stored_data_news_enabled);

const fallbackImage = computed(() => {
  const globalNewsFallbackImage = globals.value?.fallback_images?.find((item) => item.feature === 'news') || {};
  const customNewsFallbackImage = settings.value?.newsFallbackImage || {};
  const image = Object.keys(customNewsFallbackImage).length > 0 ? customNewsFallbackImage : globalNewsFallbackImage;
  return { ...image, alt: image.alt_text || '' };
});

async function fetchContentFromS3AndValidate() {
  try {
    const s3UrlForContent = mainStore.getS3UrlForMedia('news');
    const contentResponse = await nuxtGlobs.staticFetch.get(s3UrlForContent);

    const lastS3FileUpdate = contentResponse.data?.meta?.last_static_update;
    const lastMediaUpdate =
      staticFilesConfig.value?.last_static_update?.news || staticFilesConfig.value?.homepage?.last_static_update?.news;
    const isStaticDataFresh = HomepageFetchHelper.validateLastStaticUpdate(lastS3FileUpdate, lastMediaUpdate);
    const newsArticles = contentResponse?.data?.articles;

    if (!isStaticDataFresh || !newsArticles) {
      return await fetchContentFromThrillshare();
    }

    return newsArticles;
  } catch (e) {
    console.error(e);
    return await fetchContentFromThrillshare();
  }
}

async function fetchContentFromThrillshare() {
  try {
    const { url, params } = mainStore.getNews(nuxtGlobs.axios.defaults.baseURL);
    const contentResponse = await nuxtGlobs.axios.get(url, { params, cache: { ignoreCache: false } });
    return contentResponse?.data?.articles || [];
  } catch (e) {
    console.error(e);
    return [];
  }
}

function setupThemePreview() {
  content.value = defaultData.value;
  loading.value = false;
}

async function getContent() {
  if (isThemePreview.value) {
    setupThemePreview();
    return;
  }

  if (useS3ForMedia.value) {
    content.value = await fetchContentFromS3AndValidate();
  } else {
    content.value = await fetchContentFromThrillshare();
  }
  loading.value = false;
}

onMounted(() => {
  const inst = getCurrentInstance();
  const p = inst?.proxy;
  const gp = inst?.appContext.config.globalProperties;
  nuxtGlobs.axios = p?.$axios ?? gp?.$axios;
  nuxtGlobs.staticFetch = p?.$staticFetch ?? gp?.$staticFetch;
  config.value = homepage.value.news || {};
  getContent();
});

defineExpose({
  content,
  config,
  loading,
  getContent,
  setupThemePreview,
});
</script>

<style>
#news-container {
  height: 100%;
}
</style>
